import { cn } from "../utils";

/**
 * Where `query` falls in a shown label, case-insensitively.
 * Hyphens in the query also read as spaces, since handles hyphenate names.
 */
function matchIndex(label: string, query: string): { start: number; end: number } | null {
  if (!query) return null;
  const lower = label.toLowerCase();
  const needle = query.toLowerCase();
  let start = lower.indexOf(needle);
  if (start < 0) start = lower.indexOf(needle.replace(/-/g, " "));
  return start < 0 ? null : { start, end: start + needle.length };
}

/** Whether `query` falls in `label`, for filtering rather than rendering a match. */
export function isMatch(label: string, query: string): boolean {
  return matchIndex(label, query) !== null;
}

export interface MatchedTextProps {
  text: string;
  /** Search text; the matching part of `text` is emphasised. */
  query: string;
  /** Whether the row is highlighted, which changes the muted colour. */
  selected?: boolean | undefined;
  /** Native tooltip, so a truncated label can still be read in full. */
  title?: string | undefined;
  className?: string | undefined;
}

/** A list row's label with the part matching the search emphasised. */
export function MatchedText({ text, query, selected = false, title, className }: MatchedTextProps) {
  const match = matchIndex(text, query);
  if (!match)
    return (
      <span className={cn("truncate", className)} title={title}>
        {text}
      </span>
    );
  const { start, end } = match;
  return (
    // Muted grey falls below AA on the highlighted row's accent background.
    <span
      className={cn(
        "truncate",
        selected ? "text-foreground/70" : "text-muted-foreground",
        className,
      )}
      title={title}
    >
      {text.slice(0, start)}
      <span className="font-semibold text-foreground">{text.slice(start, end)}</span>
      {text.slice(end)}
    </span>
  );
}
